import { useCallback } from 'react';

import type { TranslationKey, TranslationPluralKey } from '@change-corgi/core/i18n';
import { translationKey } from '@change-corgi/core/i18n';
import { useI18n } from '@change-corgi/core-react/i18n';

import { usePsfSharePageData } from '#src/app/pages/psf/share/pageContext';

import { useReplacements } from './useReplacements';

const DEFAULT_COPY = {
	headingText: translationKey('fe.components.psf.share.initial_headline'),
	// inconsistencies between version of subheadingText, some being pluralized, some not
	subheadingText: translationKey('fe.components.psf.share.initial_subheading'),
	progressBarLabelText: translationKey('fe.components.psf.progress_bar_label'),
	skipLinkText: translationKey('fe.components.psf.share.footer_link_text'),
};

type DefaultCopy = typeof DEFAULT_COPY;
export type TranslationName = keyof DefaultCopy;

type Result = ModelHookResult<
	{ keys: DefaultCopy },
	{ translateKey: (name: TranslationName, key: TranslationKey | TranslationPluralKey) => string }
>;

export function useCopyI18nKeys(): Result {
	const { typedTranslate, typedTranslatePlural } = useI18n();
	const {
		data: { signaturesFromShareCountEstimate },
	} = usePsfSharePageData();
	const {
		data: { replacements },
	} = useReplacements();

	return {
		data: {
			keys: DEFAULT_COPY,
		},
		actions: {
			translateKey: useCallback(
				(name: TranslationName, key: TranslationKey | TranslationPluralKey): string => {
					// inconsistencies between version of subheadingText, some being pluralized, some not
					if (
						name === 'subheadingText' &&
						(key as unknown as string) === 'fe.components.psf.share.signature_count_subheading'
					) {
						return typedTranslatePlural(
							key as unknown as TranslationPluralKey,
							signaturesFromShareCountEstimate,
							replacements,
						);
					}
					return typedTranslate(key as unknown as TranslationKey, replacements);
				},
				[typedTranslate, typedTranslatePlural, replacements, signaturesFromShareCountEstimate],
			),
		},
	};
}
